import LZString from 'lz-string';
import mixpanel from 'mixpanel-browser';
import { useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PageNavTrackingEvents } from '@revelio/core';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import {
  claimDocumentArrival,
  useMountedOnArrivalWith,
} from '../../components/PageState/hooks/document-arrival';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { isTaxonomyFilterId } from '../constants/filter-constants';
import type { DefaultSelectionOverride } from '../contexts/defaultFilters';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  createInitialFilterConsistencyState,
  type FilterConsistencyState,
  getActiveFilterMap,
  handleReplaceFilters,
  migrateFilterSelectionsMap,
} from '../engine/filterGroupConsistency';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { isTreeFilter } from '../utils/common';
import { getConsistencyKey } from '../utils/consistencyKey';
import { decodeFiltersParam } from '../utils/savedViewUrl';
import {
  type TaxonomyFilterRemovalEvent,
  trackTaxonomyFiltersRemoved,
} from '../utils/trackTaxonomyFiltersRemoved';
import { validateParsedFilters } from '../utils/validate-parsed-filters';
import { stripDisabledActivityFilters } from '../utils/validateActivityFilters';
import { validateTaxonomyFilters } from '../utils/validateTaxonomyFilters';
import { useTaxonomyVersionToast } from './useTaxonomyVersionToast';

const trackUrlTaxonomyFiltersRemoved = (removal: TaxonomyFilterRemovalEvent) =>
  trackTaxonomyFiltersRemoved(removal, { source: 'url' });

export type DeserializeFilterFromUrlResult = {
  filterFromUrl: FilterConsistencyState | undefined;
  /** True when `filters` is present but decompress/parse fails (malformed share link). */
  urlFiltersParseFailed: boolean;
};

export const useDeserializeFilterFromUrl = ({
  revelioPageId,
  primaryFilterId: pagePrimaryFilterId,
  taxonomyConfig,
  defaultSelections,
  hydrateFromUrl = true,
}: {
  revelioPageId: RevelioPageId | string | undefined;
  primaryFilterId?: PrimaryFilterId;
  taxonomyConfig?: TaxonomyConfig;
  defaultSelections?: DefaultSelectionOverride;
  /** When false, skip reading `filters` / `filterGroups` from the URL (e.g. isolated chart preview). */
  hydrateFromUrl?: boolean;

  /** If we want to version based on the specific taxonomy selection, and not always the
   * default taxonomy, we can pass getSpecificRoleTaxonomyVersion to validateRoleFilters
   * or getSpecificSkillTaxonomyVersion to validateSkillFilters
   * and check the specific taxonomy version. */
  getSpecificRoleTaxonomyVersion?: (
    taxonomySelection: CustomRoleTaxonomySelection
  ) => string | undefined;
  getSpecificSkillTaxonomyVersion?: (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => string | undefined;
}): DeserializeFilterFromUrlResult => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const showTaxonomyVersionToast = useTaxonomyVersionToast();
  const roleTaxonomyVersion = taxonomyConfig?.roleTaxonomyVersion;
  const skillTaxonomyVersion = taxonomyConfig?.skillTaxonomyVersion;
  const activityTaxonomyVersion = taxonomyConfig?.activityTaxonomyVersion;
  const activityTaxonomyEnabled = taxonomyConfig?.activityTaxonomyEnabled;
  const filtersSearchValue = searchParams.get('filters');
  const filterGroupsSearchValue = searchParams.get('filterGroups');
  // Decided at mount: this provider lives on across analytics template
  // switches and the memo below re-parses whenever the URL sync rewrites the
  // param, and none of that is a deeplink — only a mount at the document's
  // arrival that already carried the param is one.
  const mountedOnArrival = useMountedOnArrivalWith(
    filtersSearchValue !== null || filterGroupsSearchValue !== null
  );

  // Ref-guarded because the memo below recomputes when taxonomy versions load —
  // a deeplink arrival should be recorded once, and only after a successful
  // parse.
  const hasTrackedDeeplink = useRef(false);
  const trackDeeplink = (deeplinkParam: 'filterGroups' | 'filters') => {
    if (hasTrackedDeeplink.current) return;
    hasTrackedDeeplink.current = true;
    if (!mountedOnArrival) return;
    const arrival = claimDocumentArrival(deeplinkParam);
    if (!arrival.isDocumentArrival) return;
    mixpanel.track(PageNavTrackingEvents.NAVIGATION_INITIATED, {
      navigation_source: 'deeplink',
      deeplink_param: deeplinkParam,
      path: pathname,
      navigation_type: arrival.navigation_type,
      referrer_host: arrival.referrer_host,
    });
  };

  // taxonomyConfig from FilterProvider is a new object each render; the
  // destructured version strings + activity flag are the real deps.
  // biome-ignore lint/correctness/useExhaustiveDependencies: taxonomyConfig fields are fully covered by the destructured taxonomy version/flag deps
  return useMemo(() => {
    type FilterGroupsOutcome =
      | { kind: 'absent' }
      | { kind: 'ok'; state: FilterConsistencyState }
      | { kind: 'invalid' };

    if (!hydrateFromUrl) {
      return {
        filterFromUrl: undefined,
        urlFiltersParseFailed: false,
      };
    }

    const deserializeFromFilterGroups = (): FilterGroupsOutcome => {
      const filterGroupsFromUrl = filterGroupsSearchValue;
      if (!filterGroupsFromUrl) return { kind: 'absent' };

      const filterGroupsDeserialized =
        LZString.decompressFromEncodedURIComponent(filterGroupsFromUrl);
      if (!filterGroupsDeserialized) return { kind: 'invalid' };

      let filterGroups: [string, [FilterId, Filter][]][];
      try {
        const parsed: unknown = JSON.parse(filterGroupsDeserialized);
        const groups = parseDecodedFilterGroupsPayload(parsed);
        if (groups === null) return { kind: 'invalid' };
        filterGroups = groups;
      } catch {
        return { kind: 'invalid' };
      }

      try {
        const primaryGroupIndex = (() => {
          if (pagePrimaryFilterId) {
            return filterGroups.findIndex(([, filterPairs]) =>
              filterPairs.some(
                ([filterId, filter]) =>
                  filterId === pagePrimaryFilterId &&
                  isTreeFilter(filter) &&
                  filter.isPrimary === true
              )
            );
          }

          const index = filterGroups.findIndex(([, filterPairs]) =>
            filterPairs.some(
              ([_, filter]) => isTreeFilter(filter) && filter.isPrimary === true
            )
          );
          return index >= 0 ? index : 0;
        })();

        const primaryFilterGroup = filterGroups?.[primaryGroupIndex];
        if (!primaryFilterGroup) return { kind: 'invalid' };

        const initialFilterState = createFilterState({
          revelioPageId,
          filterPairs: primaryFilterGroup[1],
          taxonomyConfig,
          defaultSelections,
          primaryFilterId: pagePrimaryFilterId,
          showTaxonomyVersionToast,
        });

        const newGroupMap = new Map(initialFilterState.groupMap);
        for (let i = 0; i < filterGroups.length; i++) {
          if (i === primaryGroupIndex) continue;

          const additionalFilterGroup = filterGroups[i];
          if (!additionalFilterGroup) continue;

          // Each group carries its own primary filter. Naming it lets the
          // primary-selection path run for this group too, so a legacy link's
          // `isActive` flags are honoured and the entity cap applied here as
          // well — not just on the group the page opens with.
          const additionalPrimaryFilterId = additionalFilterGroup[1].find(
            ([, filter]) => isTreeFilter(filter) && filter.isPrimary === true
          )?.[0];

          const taxonomyValidatedFilters = validateTaxonomyFilters({
            filters: stripDisabledActivityFilters(
              additionalFilterGroup[1],
              taxonomyConfig
            ),
            taxonomyConfig: taxonomyConfig ?? {},
            showTaxonomyVersionToast,
            onTaxonomyFiltersRemoved: trackUrlTaxonomyFiltersRemoved,
          });
          const additionalFilterGroupMap = validateParsedFilters(
            taxonomyValidatedFilters,
            {
              defaultFilterMap: new Map(),
              primaryFilterId: additionalPrimaryFilterId,
            }
          );
          // Normalized like every other persisted map, so the group carries
          // revived date bounds rather than the raw ISO strings from the link.
          newGroupMap.set(
            additionalFilterGroup[0],
            migrateFilterSelectionsMap(additionalFilterGroupMap)
          );
        }
        return {
          kind: 'ok',
          state: {
            ...initialFilterState,
            groupMap: newGroupMap,
          },
        };
      } catch (error) {
        console.error('Failed to parse filterGroups from URL:', error);
        return { kind: 'invalid' };
      }
    };

    const filterGroupsOutcome = deserializeFromFilterGroups();
    if (filterGroupsOutcome.kind === 'ok') {
      trackDeeplink('filterGroups');
      return {
        filterFromUrl: filterGroupsOutcome.state,
        urlFiltersParseFailed: false,
      };
    }

    const filtersFromUrl = filtersSearchValue;
    if (filterGroupsOutcome.kind === 'invalid' && !filtersFromUrl) {
      return { filterFromUrl: undefined, urlFiltersParseFailed: true };
    }

    if (!filtersFromUrl) {
      return { filterFromUrl: undefined, urlFiltersParseFailed: false };
    }

    const parsed = decodeFiltersParam(filtersFromUrl);
    if (!parsed) {
      return { filterFromUrl: undefined, urlFiltersParseFailed: true };
    }

    try {
      const filteredParsed = isScreenerV2Page(revelioPageId)
        ? parsed.filter(([id]) => !isTaxonomyFilterId(id))
        : parsed;

      const filterFromUrl = createFilterState({
        revelioPageId,
        filterPairs: filteredParsed,
        taxonomyConfig,
        defaultSelections,
        primaryFilterId: pagePrimaryFilterId,
        showTaxonomyVersionToast,
      });

      trackDeeplink('filters');

      return { filterFromUrl, urlFiltersParseFailed: false };
    } catch (error) {
      console.error('Failed to parse filters from URL:', error);
      return { filterFromUrl: undefined, urlFiltersParseFailed: true };
    }
  }, [
    pathname,
    revelioPageId,
    pagePrimaryFilterId,
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    activityTaxonomyVersion,
    activityTaxonomyEnabled,
    defaultSelections,
    showTaxonomyVersionToast,
    filtersSearchValue,
    filterGroupsSearchValue,
    hydrateFromUrl,
  ]);
};

function parseDecodedFilterGroupsPayload(
  parsed: unknown
): [string, [FilterId, Filter][]][] | null {
  if (!Array.isArray(parsed)) return null;
  const groups: [string, [FilterId, Filter][]][] = [];
  for (const entry of parsed) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const [groupKeyRaw, pairsRaw] = entry;
    if (typeof groupKeyRaw !== 'string') return null;
    if (!Array.isArray(pairsRaw)) return null;
    const pairs: [FilterId, Filter][] = [];
    for (const pair of pairsRaw) {
      if (!Array.isArray(pair) || pair.length !== 2) return null;
      const [idRaw, filterRaw] = pair;
      if (!isFilterIdValue(idRaw)) return null;
      if (!isFilterValue(filterRaw)) return null;
      pairs.push([idRaw, filterRaw]);
    }
    groups.push([groupKeyRaw, pairs]);
  }
  return groups;
}

function isFilterIdValue(id: unknown): id is FilterId {
  if (typeof id !== 'string') return false;
  for (const v of Object.values(FilterId)) {
    if (v === id) return true;
  }
  return false;
}

function isFilterValue(v: unknown): v is Filter {
  if (typeof v !== 'object' || v === null) return false;
  if (!('type' in v)) return false;
  const t = Reflect.get(v, 'type');
  if (typeof t !== 'string') return false;
  return Object.values(FilterType).some((ft) => ft === t);
}

const createFilterState = ({
  revelioPageId,
  filterPairs,
  taxonomyConfig,
  defaultSelections,
  primaryFilterId: pagePrimaryFilterId,
  showTaxonomyVersionToast,
}: {
  revelioPageId: RevelioPageId | string | undefined;
  filterPairs: [FilterId, Filter][];
  taxonomyConfig?: TaxonomyConfig;
  defaultSelections?: DefaultSelectionOverride;
  primaryFilterId?: PrimaryFilterId;
  showTaxonomyVersionToast?: (id: string, filterName: string) => void;
}) => {
  const enabledFilterPairs = stripDisabledActivityFilters(
    filterPairs,
    taxonomyConfig
  );

  const primaryFilterId = enabledFilterPairs?.find(
    ([, f]: [FilterId, Filter]) => isTreeFilter(f) && f.isPrimary
  )?.[0] as PrimaryFilterId | undefined;

  const consistencyKey = getConsistencyKey({
    revelioPageId,
    primaryFilterId,
  });

  const defaultFilterState = createInitialFilterConsistencyState(
    consistencyKey,
    taxonomyConfig,
    defaultSelections
  );
  const defaultFilterMap = getActiveFilterMap(
    defaultFilterState,
    taxonomyConfig
  );

  const taxonomyValidatedFilters = validateTaxonomyFilters({
    filters: enabledFilterPairs,
    taxonomyConfig: taxonomyConfig ?? {},
    showTaxonomyVersionToast,
    onTaxonomyFiltersRemoved: trackUrlTaxonomyFiltersRemoved,
  });

  const filterMap = validateParsedFilters(taxonomyValidatedFilters, {
    defaultFilterMap,
    primaryFilterId: pagePrimaryFilterId || primaryFilterId,
  });

  return handleReplaceFilters(defaultFilterState, {
    filterMap,
    consistencyKey,
  });
};
